import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { App, Button, Descriptions, Empty, Table, Tabs, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FileProtectOutlined, ReloadOutlined, SolutionOutlined } from '@ant-design/icons';
import { listCaseActions, listOverrides, listRoomSteps, overrideEvidence, redoStep, rerequestStep, reviewOverride } from '@/api/caseActions';
import type { CaseAction, CaseOverride, RoomStep, RoomSteps } from '@/api/caseActions';
import { runIdFromPath } from '@/api/runPath';
import { useAuth } from '@/auth/OlAuthContext';
import { listDataroomInvites } from '@/api/dataroomInvites';
import type { DataroomInviteSummary } from '@/api/dataroomInvites';
import { getEntityEvidence, getEntityTimeline } from '@/api/entities';
import { normalizeApiError } from '@/api/errors';
import { getFlowIr } from '@/api/flows';
import { findRunIndexItem, getRunDecisions, getRunState } from '@/api/runs';
import type {
  DecisionChainView,
  DecisionRecordView,
  EvidenceView,
  RunIndexItem,
  RunStateView,
  TimelineEvent,
} from '@/api/types';
import type { AssistRef } from '@/api/caseAssist';
import { CaseActionModal } from '@/cases/CaseActionModal';
import { CaseAssistPanel } from '@/cases/CaseAssistPanel';
import type { CaseActionRequest } from '@/cases/CaseActionModal';
import { DocumentPreviewModal } from '@/cases/DocumentPreviewModal';
import { FamilyEvidenceCard } from '@/cases/FamilyEvidenceCard';
import { FanOutPanel } from '@/cases/FanOutPanel';
import { OverrideReviewPanel } from '@/cases/OverrideReviewPanel';
import { FAMILY_VIEWERS, groupByFamily, hasStoredFile, isDocument, isQuestionnaire } from '@/cases/evidenceModel';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { PageHeader } from '@/components/PageHeader';
import { usePageTitle } from '@/components/pageTitle';
import { EntityTimeline } from '@/entity/EntityTimeline';
import { useModuleEnabled } from '@/modules/useModules';
import { formatAbsolute } from '@/lib/time';
import { erasedOr } from '@/lib/erased';

const { Text } = Typography;

const TAB_KEYS = ['overview', 'checklist', 'documents', 'questionnaire', 'evidence', 'events', 'decisions', 'activity', 'report'];

const stack = { display: 'flex', flexDirection: 'column', gap: 8 } as const;

function statusColor(status?: string | null): string | undefined {
  const s = (status ?? '').toUpperCase();
  if (/(COMPLETED|DONE|APPROVED|SUBMITTED|ACCEPTED|CONSUMED)/.test(s)) return 'success';
  if (/(FAILED|REJECTED|REFUSED|REVOKED|EXPIRED)/.test(s)) return 'error';
  if (/(OPEN|WAITING|PENDING|ESCALATED|ACTIVE)/.test(s)) return 'processing';
  return undefined;
}

type Finding = 'ACCEPTED' | 'REJECTED';

function EvidenceCards({
  items,
  runId,
  findings,
  awaiting,
  focusHash,
  onOverride,
  onView,
}: {
  items: EvidenceView[];
  runId: string;
  findings: Map<string, { finding: Finding; oneReviewer: boolean }>;
  awaiting: Map<string, Finding>;
  focusHash?: string | null;
  onOverride: (e: EvidenceView) => void;
  onView: (e: EvidenceView) => void;
}) {
  return (
    <div style={stack}>
      {items.map((e) => (
        <FamilyEvidenceCard
          key={e.contentHash}
          evidence={e}
          runId={runId}
          finding={findings.get(e.contentHash)?.finding}
          oneReviewer={findings.get(e.contentHash)?.oneReviewer}
          awaiting={awaiting.get(e.contentHash)}
          focused={focusHash === e.contentHash}
          action={
            <>
              {hasStoredFile(e) && (
                <Button size="small" onClick={() => onView(e)} data-testid="evidence-view" style={{ marginRight: 6 }}>
                  View
                </Button>
              )}
              {!awaiting.has(e.contentHash) && (
                <Button size="small" onClick={() => onOverride(e)} data-testid="evidence-override">
                  Override
                </Button>
              )}
            </>
          }
        />
      ))}
    </div>
  );
}

const ACTION_LABEL: Record<CaseAction['action'], string> = {
  OVERRIDE: 'Override approved',
  OVERRIDE_PROPOSED: 'Override proposed',
  OVERRIDE_REJECTED: 'Override rejected',
  REDO: 'Step run again',
  REREQUEST: 'Step re-requested',
  FOUR_EYES_PROPOSED: 'Decision proposed',
  FOUR_EYES_CONFIRMED: 'Decision confirmed',
  FOUR_EYES_DISAGREED: 'Proposal disagreed with',
  FOUR_EYES_ESCALATED: 'Disagreement sent to a third reviewer',
  FOUR_EYES_RESOLVED: 'Third reviewer decided',
};

/** An OVERRIDE row with no proposal behind it was recorded by one person, before second review. */
const isOneReviewerOverride = (a: CaseAction) => a.action === 'OVERRIDE' && !a.targetRef;

function actionDetail(a: CaseAction): string {
  if (a.action === 'OVERRIDE' || a.action === 'OVERRIDE_PROPOSED' || a.action === 'OVERRIDE_REJECTED') {
    const n = a.evidenceHashes.length;
    const records = `${n} evidence record${n === 1 ? '' : 's'}`;
    const by = typeof a.result?.proposedBy === 'string' ? ` Proposed by ${a.result.proposedBy}.` : '';
    if (a.action === 'OVERRIDE_PROPOSED') return `${a.finding === 'REJECTED' ? 'Reject' : 'Accept'} ${records}: no effect until a second reviewer approves`;
    if (a.action === 'OVERRIDE_REJECTED') return `Would have ${a.finding === 'REJECTED' ? 'rejected' : 'accepted'} ${records}.${by}`;
    return `${a.finding === 'REJECTED' ? 'Rejected' : 'Accepted'} ${records}${isOneReviewerOverride(a) ? ', recorded by one reviewer before second review applied' : `.${by}`}`;
  }
  if (a.action === 'REDO' || a.action.startsWith('FOUR_EYES')) return `${a.targetRef ?? ''}: ${a.outcome ?? ''}`;
  return a.targetRef ?? '';
}

const actionColumns = (stepTitle: (taskId: string) => string | undefined): ColumnsType<CaseAction> => [
  { title: 'Action', key: 'action', width: 150, render: (_, a) => (isOneReviewerOverride(a) ? 'Override' : ACTION_LABEL[a.action]) },
  {
    title: 'Detail',
    key: 'detail',
    width: 240,
    render: (_, a) => erasedOr((a.action === 'REREQUEST' && a.targetRef && stepTitle(a.targetRef)) || actionDetail(a)),
  },
  { title: 'Reason', dataIndex: 'reason', render: (v: string) => erasedOr(v) },
  { title: 'By', dataIndex: 'actor', width: 190 },
  { title: 'When', key: 'at', width: 190, render: (_, a) => formatAbsolute(a.createdAt) },
];

interface StepRow {
  key: string;
  step: string;
  type: string;
  status: string;
}

/** The ids of a flow's capability steps, from its IR. Empty when the IR cannot be read. */
function capabilityStepsOf(irJson: string | undefined): ReadonlySet<string> {
  if (!irJson) return new Set();
  try {
    const nodes = (JSON.parse(irJson) as { nodes?: { id: string; kind: string }[] }).nodes ?? [];
    return new Set(nodes.filter((n) => n.kind === 'capability').map((n) => n.id));
  } catch {
    return new Set();
  }
}

const STEP_COLUMNS: ColumnsType<StepRow> = [
  { title: 'Step', dataIndex: 'step', render: (v: string) => <Text className="ol-mono" style={{ fontSize: 12 }}>{v}</Text> },
  { title: 'Type', dataIndex: 'type', width: 140 },
  { title: 'Status', dataIndex: 'status', width: 160, render: (v: string) => <Tag bordered={false} color={statusColor(v)}>{v}</Tag> },
];

const INVITE_COLUMNS: ColumnsType<DataroomInviteSummary> = [
  { title: 'Data-room invite', key: 'ref', render: (_, r) => erasedOr(r.caseRef ?? r.applicantLabel ?? r.inviteId) },
  { title: 'Steps', dataIndex: 'taskCount', width: 80 },
  { title: 'Status', dataIndex: 'status', width: 130, render: (v: string) => <Tag bordered={false} color={statusColor(v)}>{v}</Tag> },
  { title: 'Sent', key: 'created', width: 190, render: (_, r) => formatAbsolute(r.createdAt) },
];

const DECISION_COLUMNS: ColumnsType<DecisionRecordView> = [
  { title: '#', dataIndex: 'seq', width: 50 },
  { title: 'Step', dataIndex: 'nodeId' },
  { title: 'Path', dataIndex: 'pathType', width: 130 },
  { title: 'Verdict', key: 'verdict', width: 150, render: (_, r) => r.policyVerdict ?? r.fallbackVerdict ?? '—' },
  { title: 'When', key: 'at', width: 190, render: (_, r) => (r.occurredAt ? formatAbsolute(r.occurredAt) : '—') },
];

/**
 * Case detail (OL-26): one page for one run and its subject. It reads only the published run,
 * evidence, timeline and invite surfaces, and nothing here knows a flow template or a check slug, so
 * a new template appears in every tab with no console change. Actions go through the proposed case
 * paths, and each one needs a reason and lands in the decision history.
 */
export function CaseDetailPage() {
  const { runId: runParam = '' } = useParams();
  const runId = runIdFromPath(runParam);
  const [params, setParams] = useSearchParams();
  usePageTitle(useMemo(() => `Case ${runId}`, [runId]));
  const reportsEnabled = useModuleEnabled('verityx') === true;

  const entityHint = params.get('entity') ?? undefined;
  const requestedTab = params.get('tab') ?? 'overview';
  const tab = TAB_KEYS.includes(requestedTab) && (requestedTab !== 'report' || reportsEnabled) ? requestedTab : 'overview';

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [index, setIndex] = useState<RunIndexItem | null>(null);
  const [state, setState] = useState<RunStateView | null>(null);
  const [evidence, setEvidence] = useState<EvidenceView[]>([]);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [invites, setInvites] = useState<DataroomInviteSummary[]>([]);
  const [chain, setChain] = useState<DecisionChainView | null>(null);
  const [chainNote, setChainNote] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [actions, setActions] = useState<CaseAction[]>([]);
  const [overrides, setOverrides] = useState<CaseOverride[]>([]);
  const [roomSteps, setRoomSteps] = useState<RoomSteps[]>([]);
  const [actionRequest, setActionRequest] = useState<CaseActionRequest | null>(null);
  // Only a capability step can run again, and the run state does not say which steps are.
  const [capabilitySteps, setCapabilitySteps] = useState<ReadonlySet<string>>(new Set());
  const { message } = App.useApp();
  const { user } = useAuth();
  // A suggestion's reference opens the record it rests on: the tab, then the card scrolled to and marked.
  const [focusHash, setFocusHash] = useState<string | null>(null);
  const [assistRefresh, setAssistRefresh] = useState(0);
  const openTab = useCallback((key: string) => {
    setParams((was) => {
      const next = new URLSearchParams(was);
      next.set('tab', key);
      return next;
    }, { replace: true });
  }, [setParams]);
  const openRef = useCallback((ref: AssistRef) => {
    if (ref.type === 'EVIDENCE') {
      setFocusHash(ref.id);
      openTab('evidence');
    } else if (ref.type === 'ACTION') {
      openTab('decisions');
    } else if (ref.type === 'STEP' || ref.type === 'ROOM_STEP') {
      openTab('checklist');
    } else {
      openTab('overview');
    }
  }, [openTab]);
  useEffect(() => {
    if (!focusHash || tab !== 'evidence') return;
    document.getElementById(`evidence-${focusHash}`)?.scrollIntoView?.({ block: 'center' });
  }, [focusHash, tab]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setNow(Date.now());
    const [idx, st, dec, acts, ovr] = await Promise.allSettled([
      findRunIndexItem(runId),
      getRunState(runId),
      getRunDecisions(runId),
      listCaseActions(runId),
      listOverrides(runId),
    ]);
    setActions(acts.status === 'fulfilled' ? acts.value : []);
    setOverrides(ovr.status === 'fulfilled' ? ovr.value : []);
    const item = idx.status === 'fulfilled' ? idx.value : null;
    setIndex(item);
    setState(st.status === 'fulfilled' ? st.value : null);
    const flow = st.status === 'fulfilled' ? st.value : item;
    if (flow?.flowId) {
      const ir = await getFlowIr(flow.flowId, { version: flow.flowVersion ?? undefined, form: 'json' }).catch(() => null);
      setCapabilitySteps(capabilityStepsOf(ir?.content));
    }
    if (idx.status === 'rejected' && st.status === 'rejected') {
      setLoadError(normalizeApiError(st.reason).message);
    } else if (!item && st.status === 'rejected') {
      setLoadError('No run with this id on this hub.');
    }
    if (dec.status === 'fulfilled') {
      setChain(dec.value);
      setChainNote(null);
    } else {
      setChain(null);
      setChainNote(normalizeApiError(dec.reason).message);
    }
    const entityId = item?.entityId ?? entityHint;
    if (entityId) {
      const [ev, tl, inv] = await Promise.allSettled([
        getEntityEvidence(entityId),
        getEntityTimeline(entityId, { pageSize: 200 }),
        listDataroomInvites(entityId),
      ]);
      setEvidence(ev.status === 'fulfilled' ? ev.value : []);
      setTimeline(tl.status === 'fulfilled' ? tl.value.events : []);
      const inviteList = inv.status === 'fulfilled' ? inv.value : [];
      setInvites(inviteList);
      const steps = await Promise.allSettled(inviteList.slice(0, 10).map((i) => listRoomSteps(i.inviteId)));
      setRoomSteps(steps.flatMap((s) => (s.status === 'fulfilled' ? [s.value] : [])));
      // The timeline read brings the run index up to date, so read a lagging row once more.
      if (item && !item.completedAt) {
        const fresh = await findRunIndexItem(runId).catch(() => null);
        if (fresh) setIndex(fresh);
      }
    } else {
      setEvidence([]);
      setTimeline([]);
      setInvites([]);
      setRoomSteps([]);
    }
    setLoading(false);
  }, [runId, entityHint]);

  useEffect(() => {
    void load();
  }, [load]);

  const entityId = index?.entityId ?? entityHint ?? null;
  const [viewing, setViewing] = useState<EvidenceView | null>(null);
  const closeViewer = useCallback(() => setViewing(null), []);
  const reload = useCallback(() => void load(), [load]);
  const flowId = index?.flowId ?? state?.flowId;
  const flowVersion = index?.flowVersion ?? state?.flowVersion;
  // Live state first: the index is a projection that catches up on a Workbench read, so it can lag.
  const outcome = state?.outcome ?? index?.outcome;
  const failure = state?.failure ?? index?.failure;
  const running = !index?.completedAt && !outcome;

  const steps = useMemo<StepRow[]>(() => {
    if (!state) return [];
    return [
      ...Object.entries(state.nodeStatuses ?? {}).map(([step, status]) => ({ key: `n-${step}`, step, type: 'Step', status })),
      ...Object.entries(state.humanTasks ?? {}).map(([step, status]) => ({ key: `h-${step}`, step, type: 'Human task', status })),
    ];
  }, [state]);

  // Actions arrive newest first, so the first finding seen on a record is its current one.
  const findings = useMemo(() => {
    const out = new Map<string, { finding: Finding; oneReviewer: boolean }>();
    for (const a of actions) {
      if (a.action !== 'OVERRIDE' || !a.finding) continue;
      for (const h of a.evidenceHashes) if (!out.has(h)) out.set(h, { finding: a.finding, oneReviewer: isOneReviewerOverride(a) });
    }
    return out;
  }, [actions]);
  // A proposal is shown beside the evidence and changes nothing: only an approved one is a finding.
  const awaiting = useMemo(() => {
    const out = new Map<string, Finding>();
    for (const o of overrides) {
      if (o.status === 'PROPOSED') for (const h of o.evidenceHashes) out.set(h, o.finding);
    }
    return out;
  }, [overrides]);

  const askOverride = (e: EvidenceView) =>
    setActionRequest({
      kind: 'override',
      title: 'Propose an override',
      run: (finding, reason) => overrideEvidence(runId, [e.contentHash], finding, reason),
    });
  const askReview = (o: CaseOverride, answer: 'approve' | 'reject') =>
    setActionRequest(
      answer === 'approve'
        ? { kind: 'approve-override', title: 'Approve this override', run: (note: string) => reviewOverride(runId, o.overrideId, 'approve', note) }
        : { kind: 'reject-override', title: 'Reject this override', run: (note: string) => reviewOverride(runId, o.overrideId, 'reject', note) },
    );
  const cards = (list: EvidenceView[]) => (
    <EvidenceCards items={list} runId={runId} findings={findings} awaiting={awaiting} focusHash={focusHash} onOverride={askOverride} onView={setViewing} />
  );
  const stepColumns: ColumnsType<StepRow> = [
    ...STEP_COLUMNS,
    {
      title: '',
      key: 'redo',
      width: 90,
      render: (_, r) =>
        r.type === 'Step' && entityId && capabilitySteps.has(r.step) && !runId.includes('/') ? (
          <Button
            size="small"
            data-testid="step-redo"
            onClick={() =>
              setActionRequest({
                kind: 'redo',
                title: `Run ${r.step} again`,
                run: (reason) => redoStep(runId, r.step, reason),
              })
            }
          >
            Redo
          </Button>
        ) : null,
    },
  ];
  const roomStepColumns = (inviteId: string): ColumnsType<RoomStep> => [
    { title: 'Room step', key: 'step', render: (_, s) => erasedOr(s.title) },
    { title: 'Kind', dataIndex: 'kind', width: 150 },
    {
      title: 'Status',
      key: 'status',
      width: 200,
      render: (_, s) => (
        <span data-testid="room-step-status">
          <Tag bordered={false} color={statusColor(s.status)}>{s.status}</Tag>
          {s.reopenCount > 0 && <Text style={{ fontSize: 11.5 }}>Re-requested {s.reopenCount}x</Text>}
        </span>
      ),
    },
    {
      title: '',
      key: 'rerequest',
      width: 110,
      render: (_, s) =>
        s.status === 'PENDING' ? null : (
          <Button
            size="small"
            data-testid="step-rerequest"
            onClick={() =>
              setActionRequest({
                kind: 'rerequest',
                title: `Ask again: ${erasedOr(s.title)}`,
                run: (reason, applicantMessage) => rerequestStep(inviteId, s.taskId, reason, applicantMessage),
              })
            }
          >
            Re-request
          </Button>
        ),
    },
  ];

  const groups = useMemo(() => groupByFamily(evidence), [evidence]);
  const documents = useMemo(() => evidence.filter(isDocument), [evidence]);
  const questionnaire = useMemo(() => evidence.filter(isQuestionnaire), [evidence]);
  const decisionEvents = useMemo(
    () => timeline.filter((t) => t.kind === 'DECISION_RECORDED' || t.kind === 'WORK_ITEM'),
    [timeline],
  );

  if (loadError && !loading) {
    return (
      <div className="ol-page">
        <ErrorState what="this case" detail={loadError} onRetry={() => void load()} />
      </div>
    );
  }

  const noEntity = (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="This run has no subject bound, so it holds no subject records." />
  );
  const none = (what: string) => <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={what} />;

  const overview = (
    <div style={{ ...stack, gap: 16 }}>
      <Descriptions size="small" column={{ xs: 1, md: 2 }} bordered>
        <Descriptions.Item label="Flow">{flowId ?? '—'}{flowVersion ? ` v${flowVersion}` : ''}</Descriptions.Item>
        <Descriptions.Item label="Status">
          <Tag bordered={false} color={running ? 'processing' : statusColor(outcome) ?? 'success'} data-testid="case-status">
            {running ? 'Running' : outcome ?? 'Finished'}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Subject">
          {entityId ? <Link to={`/entities/${encodeURIComponent(entityId)}`} className="ol-mono">{entityId}</Link> : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Started">{index?.startedAt ? formatAbsolute(index.startedAt) : '—'}</Descriptions.Item>
        <Descriptions.Item label="Completed">{index?.completedAt ? formatAbsolute(index.completedAt) : '—'}</Descriptions.Item>
        <Descriptions.Item label="Evidence records">{evidence.length}</Descriptions.Item>
        {failure && <Descriptions.Item label="Failure" span={2}>{failure}</Descriptions.Item>}
      </Descriptions>
      {groups.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {groups.map((g) => (
            <div className="ol-card" key={g.family ?? 'other'} style={{ padding: '10px 14px', minWidth: 150 }}>
              <div style={{ fontSize: 11.5, color: 'var(--ol-text-secondary)' }}>{g.family ? FAMILY_VIEWERS[g.family].label : 'Other evidence'}</div>
              <div style={{ fontSize: 18, fontWeight: 600 }}>{g.items.length}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const checklist = (
    <div style={{ ...stack, gap: 16 }}>
      <FanOutPanel fanOuts={state?.fanOuts} />
      {steps.length > 0 ? (
        <Table<StepRow> size="small" rowKey="key" pagination={false} columns={stepColumns} dataSource={steps} />
      ) : (
        none('No step has finished yet, or this run\'s live state is no longer held.')
      )}
      {invites.length > 0 && (
        <Table<DataroomInviteSummary> size="small" rowKey="inviteId" pagination={false} columns={INVITE_COLUMNS} dataSource={invites} />
      )}
      {roomSteps.filter((r) => r.steps.length > 0).map((r) => (
        <Table<RoomStep>
          key={r.inviteId}
          size="small"
          rowKey="taskId"
          pagination={false}
          columns={roomStepColumns(r.inviteId)}
          dataSource={r.steps}
        />
      ))}
    </div>
  );

  const events = state ? (
    <div style={stack}>
      {steps.length === 0 && (state.rejectedSignals ?? []).length === 0 && none('No events recorded on this run yet.')}
      {steps.map((s) => (
        <div key={s.key} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 12.5 }}>
          <Tag bordered={false} color={statusColor(s.status)}>{s.status}</Tag>
          <span>{s.type}</span>
          <Text className="ol-mono" style={{ fontSize: 12 }}>{s.step}</Text>
        </div>
      ))}
      {(state.rejectedSignals ?? []).map((r) => (
        <div key={r} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 12.5 }}>
          <Tag bordered={false} color="error">Rejected signal</Tag>
          <span>{r}</span>
        </div>
      ))}
    </div>
  ) : (
    none('This run\'s live state is no longer held. The Overview tab shows its durable record.')
  );

  const decisions = (
    <div style={{ ...stack, gap: 16 }}>
      {actions.length > 0 && (
        <Table<CaseAction> size="small" rowKey="actionId" pagination={false} columns={actionColumns((id) => roomSteps.flatMap((r) => r.steps).find((s) => s.taskId === id)?.title)} dataSource={actions} data-testid="case-actions" />
      )}
      {chain && chain.length > 0 && (
        <div>
          <Tag bordered={false} color={chain.valid ? 'success' : 'error'} data-testid="chain-status">
            {chain.valid ? 'Decision chain verified' : `Decision chain broken at ${chain.brokenSeq ?? '?'}`}
          </Tag>
          <Text style={{ fontSize: 12, color: 'var(--ol-text-secondary)' }}>{chain.length} recorded</Text>
        </div>
      )}
      {chain && chain.records.length > 0 && (
        <Table<DecisionRecordView> size="small" rowKey="seq" pagination={false} columns={DECISION_COLUMNS} dataSource={chain.records} />
      )}
      {chainNote && <Text style={{ fontSize: 12, color: 'var(--ol-text-secondary)' }}>The decision ledger is not available to you here: {chainNote}</Text>}
      {decisionEvents.length > 0 ? (
        <EntityTimeline events={decisionEvents} now={now} />
      ) : (
        !chain?.records.length && actions.length === 0 && none('No decisions recorded for this case yet.')
      )}
    </div>
  );

  const report = (
    <EmptyState
      compact
      icon={<FileProtectOutlined />}
      title="No case report ordered"
      description="The case report is a VerityX report over this case's evidence and activity log. It is ordered from here once report ordering is connected."
    />
  );

  const items: Array<{ key: string; label: string; children: ReactNode }> = [
    { key: 'overview', label: 'Overview', children: overview },
    { key: 'checklist', label: 'Checklist', children: checklist },
    { key: 'documents', label: `Documents (${documents.length})`, children: !entityId ? noEntity : documents.length ? cards(documents) : none('No documents on this subject yet.') },
    { key: 'questionnaire', label: `Questionnaire (${questionnaire.length})`, children: !entityId ? noEntity : questionnaire.length ? cards(questionnaire) : none('No answers or consents on this subject yet.') },
    {
      key: 'evidence',
      label: `Evidence (${evidence.length})`,
      children: !entityId ? noEntity : groups.length ? (
        <div style={{ ...stack, gap: 18 }}>
          {groups.map((g) => (
            <section key={g.family ?? 'other'} aria-label={g.family ? FAMILY_VIEWERS[g.family].label : 'Other evidence'}>
              <h3 style={{ fontSize: 13, margin: '0 0 8px' }}>{g.family ? FAMILY_VIEWERS[g.family].label : 'Other evidence'}</h3>
              {cards(g.items)}
            </section>
          ))}
        </div>
      ) : none('No evidence on this subject yet.'),
    },
    { key: 'events', label: 'Events', children: events },
    { key: 'decisions', label: 'Decision history', children: decisions },
    { key: 'activity', label: 'Activity log', children: !entityId ? noEntity : timeline.length ? <EntityTimeline events={timeline} now={now} /> : none('No activity on this subject yet.') },
    ...(reportsEnabled ? [{ key: 'report', label: 'Report', children: report }] : []),
  ];

  return (
    <div className="ol-page">
      <PageHeader
        icon={<SolutionOutlined />}
        title={`Case · ${flowId ?? runId}`}
        subtitle={<Text className="ol-mono" style={{ fontSize: 12 }}>{runId}</Text>}
        extra={
          <Button icon={<ReloadOutlined />} loading={loading} onClick={() => { setAssistRefresh((n) => n + 1); void load(); }}>
            Refresh
          </Button>
        }
      />
      <OverrideReviewPanel overrides={overrides} mySub={user?.sub} onReview={askReview} />
      <CaseAssistPanel runId={runId} onOpen={openRef} refreshKey={assistRefresh} />
      <Tabs
        activeKey={tab}
        onChange={(key) => {
          const next = new URLSearchParams(params);
          next.set('tab', key);
          setParams(next, { replace: true });
        }}
        items={items}
      />
      {viewing && entityId && (
        <DocumentPreviewModal key={viewing.contentHash} entityId={entityId} evidence={viewing} onClose={closeViewer} onErased={reload} />
      )}
      <CaseActionModal
        request={actionRequest}
        onClose={() => setActionRequest(null)}
        onDone={() => {
          message.success('Recorded in the decision history.');
          setAssistRefresh((n) => n + 1);
          void load();
        }}
      />
    </div>
  );
}
